import { Injectable } from '@angular/core'
import { Actions, Effect, ofType } from '@ngrx/effects'
import { ROUTER_NAVIGATION, RouterNavigationAction } from '@ngrx/router-store'
import { Store } from '@ngrx/store'
import { of } from 'rxjs/observable/of'
import { catchError, debounceTime, exhaustMap, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators'
import { environment } from '../../../../../environments/environment'
import { VendorTeamImageV1 } from '../../../../api/api/models/vendor-team-image-v1'
import { VendorTeamLogoImageV1 } from '../../../../api/api/models/vendor-team-logo-image-v1'
import { VendorTeamV1 } from '../../../../api/api/models/vendor-team-v1'
import { VendorTeamsV1 } from '../../../../api/api/models/vendor-teams-v1'

import { AppState } from '../../../../store'
import { RouterStateUrl } from '../../../../app.routing'
import * as fromTeam from '../selectors/team.selectors'
import * as App from '../../../../store/actions/app.actions'
import * as Team from '../actions/team.actions'

import { JeanService } from '../../../../api/api/services/jean.service'
import { TeamImage } from '../../../../teams/team-info/team-image'
import { NotificationService } from '../../../notifications/notification.service'
import { VendorTeamUpdatedIE } from '../../../signalR/models/integration-events/vendor-team-updated-ie'

@Injectable()
export class TeamEffects {

  constructor(private actions$: Actions,
              private store: Store<AppState>,
              private jeanService: JeanService,
              private notificationService: NotificationService
              ) {
  }

  @Effect()
  routeChanged$ = this.actions$.pipe(
    ofType(ROUTER_NAVIGATION),
    map((action: RouterNavigationAction<RouterStateUrl>) => action.payload.routerState),
    filter((routerState: RouterStateUrl) => routerState.module === 'teams'),
    map((routerState: RouterStateUrl) => this.getTeamId(routerState)),
    withLatestFrom(this.store.select(fromTeam.getEditingTeamIdSelector)),
    map(([selectingTeamId, editingTeamId]) => ({selectingTeamId, editingTeamId})),
    filter(({selectingTeamId, editingTeamId}) => !!selectingTeamId && selectingTeamId !== editingTeamId),
    switchMap(({selectingTeamId}) => of(new Team.SelectTeamForEditing({selectingTeamId}))))

  @Effect()
  uploadTeamImage$ = this.actions$.pipe(
    ofType(Team.UPLOAD_TEAM_IMAGE),
    debounceTime(environment.debounceTime),
    map((action: Team.UploadTeamImage) => action.payload.image),
    switchMap((image: TeamImage) =>
      this.jeanService
        .UploadVendorTeamImage({
          model: {base64Image: image.base64Img},
          vendorTeamId: image.team.id,
          version: '1',
        }).pipe(
          mergeMap((res: VendorTeamImageV1) => [
            new App.ApiCallSuccess({
              title: 'Uploaded',
              msg: 'Team image was uploaded.'
            }),
            new Team.UploadTeamImageSuccess({vendorTeamId: image.team.id, imgUrl: res.imgUrl})
          ]),
          catchError(error => of(new App.ApiCallFailure({
            title: 'Error',
            msg: 'Could not upload team image'
        }))))
    ))

  @Effect()
  uploadTeamLogo$ = this.actions$.pipe(
    ofType(Team.UPLOAD_TEAM_LOGO),
    debounceTime(environment.debounceTime),
    map((action: Team.UploadTeamLogo) => action.payload.image),
    switchMap((image: TeamImage) =>
      this.jeanService
        .UploadVendorLogoImage({
          model: {base64Image: image.base64Img},
          vendorTeamId: image.team.id,
          version: '1',
        }).pipe(
        mergeMap((res: VendorTeamLogoImageV1) => [
          new App.ApiCallSuccess({
            title: 'Uploaded',
            msg: 'Team logo was uploaded.'
          }),
          new Team.UploadTeamLogoSuccess({vendorTeamId: image.team.id, logoImgUrl: res.logoImgUrl})
        ]),
        catchError(error => of(new App.ApiCallFailure({
          title: 'Error',
          msg: 'Could not upload team image'
        }))))
    ))

  @Effect()
  updateTeamInfo$ = this.actions$.pipe(
    ofType(Team.UPDATE_TEAM_INFO),
    debounceTime(environment.debounceTime),
    map((action: Team.UpdateTeamInfo) => action.payload.team),
    switchMap(team =>
      this.jeanService
        .UpdateVendorTeam({
          model: {name: team.name, motto: team.motto, hasCallMeEnabled: team.hasCallMeEnabled},
          vendorTeamId: team.id,
          version: '1',
        }).pipe(
          mergeMap(_ => [
            new App.ApiCallSuccess({
              title: 'Updated',
              msg: 'Team info was updated.'
            }),
            new Team.UpdateTeamSuccess({team})
          ]),
          catchError(error => of(new App.ApiCallFailure({
            title: 'Error',
            msg: 'Could not update team info'
        }))))
    ))

  @Effect()
  createTeam$ = this.actions$.pipe(
    ofType(Team.CREATE_TEAM),
    debounceTime(environment.debounceTime),
    map((action: Team.CreateTeam) => action.payload.team),
    exhaustMap(team =>
      this.jeanService
        .CreateVendorTeam({
          model: {name: team.name, motto: team.motto, hasCallMeEnabled: team.hasCallMeEnabled},
          version: '1',
        }).pipe(
          mergeMap(newTeam => {
            return [
              new App.ApiCallSuccess({
                title: 'Created',
                msg: 'Team was created.'
              }),
              new Team.CreateTeamSuccess({team: newTeam})
            ]
          }),
          catchError(error => of(new App.ApiCallFailure({
            title: 'Error',
            msg: 'Could not create team'
        }))))
    ))

  @Effect()
  createTeamSuccess$ = this.actions$.pipe(
    ofType(Team.CREATE_TEAM_SUCCESS),
    map((action: Team.CreateTeamSuccess) => action.payload.team.id),
    mergeMap(id => [
      new Team.SelectTeamForEditing({selectingTeamId: id}),
      new App.Go({path: ['/teams/' + id, {outlets: {'tabs': ['info']}}]}),
    ]))

  @Effect()
  loadTeams$ = this.actions$.pipe(
    ofType(Team.LOAD_TEAMS),
    exhaustMap(_ =>
      this.jeanService.GetVendorTeams({version: '1', limit: 0}).pipe(
        map((res: VendorTeamsV1) => new Team.LoadTeamsSuccess({teams: res.items})),
        catchError(error => of(new App.ApiCallFailure({
          title: 'Error',
          msg: 'Could not load teams'
      }))))))

  @Effect()
  loadTeam$ = this.actions$.pipe(
    ofType(Team.LOAD_TEAM),
    exhaustMap((action: Team.LoadTeam) =>
      this.jeanService.GetVendorTeam({version: '1', vendorTeamId: action.payload.vendorTeamId}).pipe(
        map((team: VendorTeamV1) => new Team.LoadTeamSuccess({team})),
        catchError(error => of(new App.ApiCallFailure({
          title: 'Error',
          msg: 'Could not load team'
        }))))))

  @Effect()
  selectTeamForEditing$ = this.actions$.pipe(
    ofType(Team.SELECT_TEAM_FOR_EDITING),
    map((action: Team.SelectTeamForEditing) => action.payload.selectingTeamId),
    withLatestFrom(this.store.select(fromTeam.getEditingTeamIdSelector)),
    switchMap(([selectingTeamId, editingTeamId]) => of(new Team.SelectTeamForEditingFilled({
      editingTeamId,
      selectingTeamId
    }))))

  @Effect()
  selectTeamForEditingDone$ = this.actions$.pipe(
    ofType(Team.SELECT_TEAM_FOR_EDITING_FILLED),
    map((action: Team.SelectTeamForEditingFilled) => action.payload.selectingTeamId),
    switchMap((selectingTeamId) => of(new Team.SelectTeamForEditingDone({selectingTeamId})))
  )

  @Effect()
  vendorTeamUpdate$ = this.actions$.pipe(
    ofType(Team.TEAM_UPDATED_EVENT),
    map((action: Team.UpdatedTeamEvent) => {
      const {teamName} = action.payload.event
      const msg = teamName + ' <br>changed by other user'
      this.notificationService.showInfoNotification('Vendor Team Updated', msg)
      return action.payload.event
    }),
    switchMap((event: VendorTeamUpdatedIE) => of(new Team.UpdatedTeamEventFinish(event)))
  )

  private getTeamId(routerState: RouterStateUrl) {
    return routerState.params.id || routerState.url.replace('/teams/', '').split('/')[0]
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/team/store/effects/team.effects.ts